/**
 * User Context - Clean Architecture
 *
 * Manages:
 * 1. Firebase Auth user
 * 2. Global user document (/users/{uid})
 * 3. Full user profile (/clients/{clientKey}/directory/users/main/{uid})
 * 4. Client key for scoping all database operations
 * 5. Admin mirroring feature
 *
 * Usage:
 * const { user, globalUser, userProfile, clientKey, effectiveUserId } = useUser();
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { onAuthStateChanged, User } from 'firebase/auth';
import { onSnapshot, updateDoc, getDoc } from 'firebase/firestore';
import { auth } from '../firebase';
import { UserProfile, ProfileVisibility } from '../types/user';
import { GlobalUserRef_Document, UserRef_Document } from '../db';
import { getClientKey } from '../db/helpers/getClientKey';
import { clientKeyManager } from '../db/helpers/clientKeyManager';
import { PrivacyService } from '../services/privacyService';
import { refreshUserClaims } from '../services/cloudFunctions';

// Helper function to convert Firestore Timestamp to Date
const toDate = (value: any): Date => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (value.toDate && typeof value.toDate === 'function') return value.toDate();
  return new Date();
};

/* ═══════════════════════════════════════════════════════════
   TYPES
   ═══════════════════════════════════════════════════════════ */

interface GlobalUserData {
  client_key: string;
  uid: string;
  email: string;
  created_at: Date;
  updated_at: Date;
}

interface UserContextType {
  user: User | null;
  globalUser: GlobalUserData | null;
  userProfile: UserProfile | null;
  clientKey: string | null;
  loading: boolean;
  updateUserProfile: (updates: Partial<UserProfile>) => Promise<void>;

  // Email verification status (from custom claims - used for security)
  isEmailVerified: boolean;
  // Server-minted org_admin[] claim: the schools this user administers. School
  // admin power lives here, not in `role`, so a district_admin can also hold it
  // (HIERARCHY_SPEC §2 composition).
  orgAdminIds: string[];
  emailVerificationLoading: boolean;

  // Admin mirroring
  viewingRole: string | null;
  setViewingRole: (role: string | null) => void;
  mirroredUser: UserProfile | null;
  setMirroredUser: (user: UserProfile | null) => void;

  // Effective user (mirrored or actual)
  effectiveRole: string | null;
  effectiveUserProfile: UserProfile | null;
  effectiveUserId: string | null;

  // V1 Privacy: account_type removed - only profileVisibility is used
  profileVisibility: ProfileVisibility;
  updatePrivacySettings: (profileVisibility?: ProfileVisibility) => Promise<void>;
}

/* ═══════════════════════════════════════════════════════════
   CONTEXT
   ═══════════════════════════════════════════════════════════ */

const UserContext = createContext<UserContextType | null>(null);

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within UserProvider');
  }
  return context;
};

/* ═══════════════════════════════════════════════════════════
   PROVIDER
   ═══════════════════════════════════════════════════════════ */

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [globalUser, setGlobalUser] = useState<GlobalUserData | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [clientKey, setClientKey] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(true); // Auth loading state
  const [profileLoading, setProfileLoading] = useState(true); // Profile loading state
  const [viewingRole, setViewingRole] = useState<string | null>(null);
  const [mirroredUser, setMirroredUser] = useState<UserProfile | null>(null);

  // Email verification state (from custom claims)
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [orgAdminIds, setOrgAdminIds] = useState<string[]>([]);
  const [emailVerificationLoading, setEmailVerificationLoading] = useState(true);

  // Combined loading state - true if either auth or profile is loading
  const loading = authLoading || profileLoading;

  /* ───────────────────────────────────────────────────────
     AUTH STATE LISTENER
     ─────────────────────────────────────────────────────── */

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(
      auth,
      async (firebaseUser) => {
        setUser(firebaseUser);

        if (firebaseUser) {
          // Check email verification status from custom claims
          setEmailVerificationLoading(true);
          try {
            let tokenResult = await firebaseUser.getIdTokenResult();

            // CRITICAL: Check if client_key claim is present
            // If missing, trigger claims refresh and force new token
            const clientKeyClaim = tokenResult.claims.client_key as string | undefined;
            if (!clientKeyClaim) {              try {
                // Call refreshUserClaims for the current user (always allowed)
                await refreshUserClaims();
                // Force a fresh token from the server
                tokenResult = await firebaseUser.getIdTokenResult(true);              } catch (refreshError) {              }
            } else {            }

            const emailVerifiedClaim = tokenResult.claims.email_verified === true;
            setIsEmailVerified(emailVerifiedClaim);
            const orgAdminClaim = tokenResult.claims.org_admin;
            setOrgAdminIds(Array.isArray(orgAdminClaim) ? (orgAdminClaim as string[]) : []);
          } catch (error) {            setIsEmailVerified(false);
            setOrgAdminIds([]);
          }
          setEmailVerificationLoading(false);

          // Get client_key from global user
          const result = await getClientKey(firebaseUser.uid, clientKey);
          if (result.success) {
            setClientKey(result.clientKey);
            // Cache it in the singleton manager for service access
            clientKeyManager.setClientKey(result.clientKey);
          } else {            setClientKey(null);
            clientKeyManager.clearClientKey();
          }
        } else {
          // User signed out
          setGlobalUser(null);
          setUserProfile(null);
          setClientKey(null);
          setMirroredUser(null);
          setIsEmailVerified(false);
          setOrgAdminIds([]);
          setEmailVerificationLoading(false);
          setProfileLoading(false); // No profile to load when signed out
          clientKeyManager.clearClientKey();
        }

        setAuthLoading(false);
      },
      (error) => {        setUser(null);
        setGlobalUser(null);
        setUserProfile(null);
        setClientKey(null);
        setIsEmailVerified(false);
        setOrgAdminIds([]);
        setEmailVerificationLoading(false);
        setAuthLoading(false);
        setProfileLoading(false);
      }
    );

    return () => {
      unsubAuth();
    };
  }, []);

  /* ───────────────────────────────────────────────────────
     GLOBAL USER LISTENER
     ─────────────────────────────────────────────────────── */

  useEffect(() => {
    if (!user) {
      setGlobalUser(null);
      return;
    }

    const globalUserRef = GlobalUserRef_Document(user.uid);    const unsubGlobal = onSnapshot(
      globalUserRef,
      (snapshot) => {        if (snapshot.exists()) {
          const data = snapshot.data();
          setGlobalUser({
            client_key: data.client_key,
            uid: data.uid,
            email: data.email,
            created_at: toDate(data.created_at),
            updated_at: toDate(data.updated_at),
          });
          setClientKey(data.client_key);
        } else {          setGlobalUser(null);
        }
      },
      (error) => {        setGlobalUser(null);
      }
    );

    return () => {      try {
        unsubGlobal();      } catch (e) {      }
    };
  }, [user]);

  /* ───────────────────────────────────────────────────────
     FULL USER PROFILE LISTENER
     ─────────────────────────────────────────────────────── */

  useEffect(() => {
    if (!user || !clientKey) {
      setUserProfile(null);
      // Only set profileLoading to false if auth has finished loading and there's no user
      // This prevents a race condition where profileLoading becomes false before the profile listener starts
      if (!authLoading && !user) {
        setProfileLoading(false);
      }
      return;
    }

    setProfileLoading(true); // Start loading profile

    const userRef = UserRef_Document(user.uid, clientKey);    const unsubProfile = onSnapshot(
      userRef,
      async (snapshot) => {        if (snapshot.exists()) {
          const data = snapshot.data();
          setUserProfile({
            uid: data.uid,
            email: data.email,
            display_name: data.display_name,
            photo_url: data.photo_url,
            role: data.role,
            created_at: toDate(data.created_at),
            updated_at: toDate(data.updated_at),
            metadata: data.metadata || {},
            organization_id: data.organization_id,
            organization_memberships: data.organization_memberships,
            team_ids: data.team_ids || [],
            org_link_state: data.org_link_state,
            team_link_state: data.team_link_state,
            access_state_updated_at: data.access_state_updated_at ? toDate(data.access_state_updated_at) : undefined,
            access_state_reason: data.access_state_reason,
            pending_org_request_id: data.pending_org_request_id,
            pending_team_request_id: data.pending_team_request_id,
            pending_org_id: data.pending_org_id,
            pending_team_id: data.pending_team_id,
            pending_scope_updated_at: data.pending_scope_updated_at ? toDate(data.pending_scope_updated_at) : undefined,
            enabled_permissions: data.enabled_permissions,
            // Parent-child linking fields
            linked_children_ids: data.linked_children_ids || [],
            linked_parent_id: data.linked_parent_id,
            linked_parent_ids: data.linked_parent_ids || [],
            // V1 Privacy: account_type and connection_count removed
            profile_visibility: data.profile_visibility || 'public',
          });

          // Handle mirroring
          if ((data.role === 'school_admin' || data.role === 'super_admin') &&
              data.is_mirroring &&
              data.currently_mirroring_uid) {
            try {
              const mirroredUserRef = UserRef_Document(data.currently_mirroring_uid, clientKey);
              const mirroredUserSnap = await getDoc(mirroredUserRef);
              if (mirroredUserSnap.exists()) {
                const mirroredData = mirroredUserSnap.data();
                setMirroredUser({
                  uid: mirroredData.uid,
                  email: mirroredData.email,
                  display_name: mirroredData.display_name,
                  photo_url: mirroredData.photo_url,
                  role: mirroredData.role,
                  created_at: toDate(mirroredData.created_at),
                  updated_at: toDate(mirroredData.updated_at),
                  metadata: mirroredData.metadata || {},
                  organization_id: mirroredData.organization_id,
                  organization_memberships: mirroredData.organization_memberships,
                  team_ids: mirroredData.team_ids || [],
                  org_link_state: mirroredData.org_link_state,
                  team_link_state: mirroredData.team_link_state,
                  access_state_updated_at: mirroredData.access_state_updated_at ? toDate(mirroredData.access_state_updated_at) : undefined,
                  access_state_reason: mirroredData.access_state_reason,
                  pending_org_request_id: mirroredData.pending_org_request_id,
                  pending_team_request_id: mirroredData.pending_team_request_id,
                  pending_org_id: mirroredData.pending_org_id,
                  pending_team_id: mirroredData.pending_team_id,
                  pending_scope_updated_at: mirroredData.pending_scope_updated_at ? toDate(mirroredData.pending_scope_updated_at) : undefined,
                  enabled_permissions: mirroredData.enabled_permissions,
                  // V1 Privacy: account_type and connection_count removed
                  profile_visibility: mirroredData.profile_visibility || 'public',
                });
              } else {
                // Clear invalid mirroring reference
                await updateDoc(userRef, {
                  is_mirroring: false,
                  currently_mirroring_uid: null,
                  currently_mirroring_name: null
                });
                setMirroredUser(null);
              }
            } catch (error) {              setMirroredUser(null);
            }
          } else {
            setMirroredUser(null);
          }

          // Check if server requested token refresh (for critical permission changes)
          const lastRefresh = localStorage.getItem('last_token_refresh');
          const serverRefreshTime = data.metadata?.force_token_refresh_at;

          if (serverRefreshTime) {
            const refreshTimestamp = toDate(serverRefreshTime).getTime();
            const lastRefreshTimestamp = lastRefresh ? parseInt(lastRefresh, 10) : 0;

            // If server refresh is newer than our last refresh, force token refresh
            if (refreshTimestamp > lastRefreshTimestamp) {              user.getIdToken(true).then(() => {
                localStorage.setItem('last_token_refresh', refreshTimestamp.toString());                window.location.reload();
              }).catch(err => {              });
            }
          }

          // Profile fully loaded (including mirrored user if applicable)
          setProfileLoading(false);

        } else {          setUserProfile(null);
          setMirroredUser(null);
          setProfileLoading(false);
        }
      },
      (error) => {        setUserProfile(null);
        setMirroredUser(null);
        setProfileLoading(false);
      }
    );

    return () => {      try {
        unsubProfile();      } catch (e) {      }
    };
  }, [user, clientKey]);

  /* ───────────────────────────────────────────────────────
     UPDATE USER PROFILE
     ─────────────────────────────────────────────────────── */

  const updateUserProfile = async (updates: Partial<UserProfile>) => {
    if (!user || !clientKey) return;

    const userRef = UserRef_Document(user.uid, clientKey);
    await updateDoc(userRef, {
      ...updates,
      updated_at: new Date()
    });
  };

  /* ───────────────────────────────────────────────────────
     MIRRORING HELPER
     ─────────────────────────────────────────────────────── */

  const setMirroredUserWithPersistence = async (targetUser: UserProfile | null) => {
    if (!userProfile?.uid || (userProfile.role !== 'school_admin' && userProfile.role !== 'super_admin')) {      return;
    }

    try {
      if (targetUser) {
        const { startMirroringUser } = await import('../services/cloudFunctions');
        const targetUserName = targetUser.display_name || targetUser.email || 'Unknown User';

        await startMirroringUser({
          targetUserId: targetUser.uid,
          targetUserName
        });      } else {
        const { stopMirroringUser } = await import('../services/cloudFunctions');
        await stopMirroringUser();      }
    } catch (error) {      throw error;
    }
  };

  /* ───────────────────────────────────────────────────────
     UPDATE PRIVACY SETTINGS
     ─────────────────────────────────────────────────────── */

  // V1 Privacy: accountType param removed
  const updatePrivacySettings = async (
    profileVisibility?: ProfileVisibility
  ) => {
    if (!user || !clientKey) {
      throw new Error('User must be logged in to update privacy settings');
    }

    try {
      // V1 Privacy: accountType removed - only profileVisibility is used
      if (profileVisibility !== undefined) {
        await PrivacyService.updateProfileVisibility(user.uid, profileVisibility, clientKey);
      }
    } catch (error) {      throw error;
    }
  };

  /* ───────────────────────────────────────────────────────
     COMPUTED VALUES
     ─────────────────────────────────────────────────────── */

  const effectiveUserProfile = mirroredUser || userProfile;
  const effectiveUserId = mirroredUser?.uid || user?.uid || null;
  const effectiveRole = userProfile?.role === 'super_admin' && viewingRole
    ? viewingRole
    : effectiveUserProfile?.role || null;

  // V1 Privacy: account_type removed - only profileVisibility is used
  const profileVisibility: ProfileVisibility = userProfile?.profile_visibility || 'public';

  /* ───────────────────────────────────────────────────────
     PROVIDER VALUE
     ─────────────────────────────────────────────────────── */

  return (
    <UserContext.Provider value={{
      user,
      globalUser,
      userProfile,
      clientKey,
      loading,
      updateUserProfile,
      isEmailVerified,
      emailVerificationLoading,
      viewingRole,
      setViewingRole,
      effectiveRole,
      mirroredUser,
      setMirroredUser: setMirroredUserWithPersistence,
      effectiveUserProfile,
      effectiveUserId,
      // V1 Privacy: account_type removed - only profileVisibility is used
      profileVisibility,
      orgAdminIds,
      updatePrivacySettings,
    }}>
      {children}
    </UserContext.Provider>
  );
};
